/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { MessageBar } from '@fluentui/react/lib/MessageBar';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';

export type EntityMigrationMessageBarProps = {
    /**
     * The count of the applications that have composite entities.
     */
    appsToUpgradeCount: number;

    /**
     * A callback to apply some action when use dismisses the message.
     */
    onDismiss: () => void;
};

const messages = defineMessages({
    generalMessage: {
        id: 'luis.entityMigrationMessageBar.generalMessage',
        defaultMessage: 'Upgrades available.'
    },
    detailedMessage: {
        id: 'luis.entityMigrationMessageBar.detailedMessage',
        defaultMessage: 'We have detected composite entities in {appsToUpgradeCount} of your apps.'
    },
    learnMore: {
        id: 'luis.entityMigrationMessageBar.learnMore',
        defaultMessage: 'Learn more about upgrading composite entities.'
    },
    dismissButtonAriaLabel: {
        id: 'luis.entityMigrationMessageBar.dismissButtonAriaLabel',
        defaultMessage: 'Dismiss'
    }
});

/**
 * Displays a message to notify users to upgrade their
 * apps that contain composite entities.
 */
export const EntityMigrationMessageBar = (props: EntityMigrationMessageBarProps) => {
    const { appsToUpgradeCount, onDismiss } = props;
    const { localize } = useLocalization();

    return (
        <MessageBar
            onDismiss={onDismiss}
            dismissButtonAriaLabel={localize(messages.dismissButtonAriaLabel)}
            aria-label={`${localize(messages.generalMessage)} ${localize(messages.detailedMessage, { appsToUpgradeCount })}`}>
            <LuisText variant="smallPlus" fontWeight="bold">
                {localize(messages.generalMessage)}
            </LuisText>
            &nbsp;
            <LuisText data-automation-id="detailedMessage" variant="smallPlus">
                {localize(messages.detailedMessage, { appsToUpgradeCount })}
            </LuisText>
            &nbsp;
            <ExternalLink href="https://go.microsoft.com/fwlink/?linkid=2128287">{localize(messages.learnMore)}</ExternalLink>
        </MessageBar>
    );
};
